<template>
  <div id="app">
    <main class="main">
      <router-view />
    </main>
  </div>
</template>

<script setup>
import { onMounted } from 'vue';
import { useAuthStore } from './stores/auth';

const authStore = useAuthStore();

onMounted(() => {
  authStore.ensureValidSession();
});
</script>

<style>
/* ─── Design System ─── */
:root {
  --bg: #f9f7f4;
  --surface: #ffffff;
  --text: #2c2a28;
  --text-secondary: #7a7570;
  --text-muted: #b0aaa3;
  --accent: #b8784e;
  --accent-hover: #9e6540;
  --border: #e8e4de;
  --code-bg: #f1ede7;

  --font-serif:
    'Cormorant Garamond', 'Songti SC', 'STSong', Georgia, 'Times New Roman',
    serif;
  --font-sans:
    -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Hiragino Sans GB',
    'Microsoft YaHei', 'Helvetica Neue', sans-serif;
  --font-mono: 'SF Mono', 'Monaco', 'Menlo', 'Consolas', monospace;

  --content-width: 720px;
  --wide-width: 960px;
  --header-height: 64px;

  --ease: cubic-bezier(0.25, 0.1, 0.25, 1);
}

/* ─── Reset ─── */
*,
*::before,
*::after {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  font-family: var(--font-sans);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background-color: var(--bg);
  color: var(--text);
  line-height: 1.6;
  font-size: 16px;
}

#app {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

a {
  color: inherit;
  text-decoration: none;
}

button {
  font-family: inherit;
  cursor: pointer;
}

/* ─── Animations ─── */
@keyframes pageIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* ─── Header ─── */
.header {
  border-bottom: 1px solid var(--border);
  background: var(--surface);
  position: sticky;
  top: 0;
  z-index: 100;
}

.header-inner {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px;
  height: var(--header-height);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.logo {
  font-family: var(--font-serif);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text);
  letter-spacing: 0.04em;
  transition: color 0.2s var(--ease);
}

.logo:hover {
  color: var(--accent);
}

/* ─── Mobile Menu Toggle ─── */
.menu-toggle {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  background: none;
  border: none;
  padding: 8px;
  z-index: 101;
}

.menu-toggle span {
  display: block;
  width: 20px;
  height: 1.5px;
  background: var(--text);
  transition: all 0.3s var(--ease);
  transform-origin: center;
}

.menu-toggle.active span:nth-child(1) {
  transform: translateY(6.5px) rotate(45deg);
}

.menu-toggle.active span:nth-child(2) {
  opacity: 0;
}

.menu-toggle.active span:nth-child(3) {
  transform: translateY(-6.5px) rotate(-45deg);
}

/* ─── Navigation ─── */
.nav {
  display: flex;
  align-items: center;
  gap: 32px;
}

.nav-link {
  color: var(--text-secondary);
  font-size: 0.9rem;
  letter-spacing: 0.02em;
  transition: color 0.2s var(--ease);
  position: relative;
}

.nav-link:hover {
  color: var(--accent);
}

.nav-link::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 0;
  width: 0;
  height: 1px;
  background: var(--accent);
  transition: width 0.3s var(--ease);
}

.nav-link:hover::after {
  width: 100%;
}

.nav-user {
  color: var(--accent);
  font-size: 0.85rem;
  font-weight: 500;
}

/* ─── Main Content ─── */
.main {
  flex: 1;
  width: 100%;
}

/* ─── Responsive ─── */
@media (max-width: 768px) {
  .header-inner {
    padding: 0 24px;
  }

  .menu-toggle {
    display: flex;
  }

  .nav {
    position: absolute;
    top: var(--header-height);
    left: 0;
    right: 0;
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    flex-direction: column;
    align-items: flex-start;
    gap: 0;
    padding: 0 24px;
    max-height: 0;
    overflow: hidden;
    transition:
      max-height 0.3s var(--ease),
      padding 0.3s var(--ease);
  }

  .nav.open {
    max-height: 300px;
    padding: 20px 24px;
    gap: 18px;
  }

  .nav-link::after {
    display: none;
  }
}
</style>
